import { Component, OnInit, TemplateRef } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  NgForm,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { TestTypes, TestVisitTypes } from 'src/app/enums/test-coupon-enums';
import { showNotification } from 'src/app/helpers/show-toast';
import { IBookTestRequestDTO } from 'src/app/_interfaces/EhrTestRequests/i-test-requests';
import { IArea } from 'src/app/_interfaces/patients/i-area';
import { ICity } from 'src/app/_interfaces/patients/i-city';
import { ILab } from 'src/app/_interfaces/patients/i-lab';
import { IMedicalTest } from 'src/app/_interfaces/patients/i-medical-test';
import { Area } from 'src/app/_models/area.model';
import { City } from 'src/app/_models/city.model';
import { FullPatient, PatientAddress } from 'src/app/_models/patient.model';
import { AreaService } from 'src/app/_services/area.service';
import { CityService } from 'src/app/_services/city.service';
import { MedicalTestRequestService } from 'src/app/_services/medical-test-request/medical-test-request.service';
import { PatientService } from 'src/app/_services/patient.service';
import { SuccessfullRequestModalComponent } from '../successfull-request-modal/successfull-request-modal.component';

@Component({
  selector: 'app-new-test-request',
  templateUrl: './new-test-request.component.html',
  styleUrls: ['./new-test-request.component.scss'],
})
export class NewTestRequestComponent implements OnInit {
  pageTitle = 'Patient Profile';
  patientId: string;
  isSubmitted = false;
  tests: IMedicalTest[] = [];
  labTests: IMedicalTest[] = [];
  radiologyTests: IMedicalTest[] = [];
  labs: ILab[] = [];
  testLabs: ILab[] = [];
  radiologyLabs: ILab[] = [];
  cities: ICity[] = [];
  medicalTestsAreas: IArea[] = [];
  radiologyTestsAreas: IArea[] = [];
  patient = {} as FullPatient;
  addresses = [];
  newAddress = {} as PatientAddress;
  testAndLocationForm: FormGroup;
  labForm: FormGroup;
  paymentForm: FormGroup;
  step = 1;
  cityList: City[];
  areaList: Area[];
  modalRef: BsModalRef;
  addAddressForm = {
    keyboard: true,
    class: 'modal-dialog-centered modal-black',
  };
  testVisitTypes = TestVisitTypes;
  selectedCity: City;
  selectedArea: Area;
  selectedAddress: PatientAddress;
  discount = 0;
  testTypes = TestTypes;
  isValidPromocode = true;
  selectedTestTypeId: number;
  availableTestTypes: { id: number, type: string }[] = [
    { id: this.testTypes.Lab, type: 'Lab Test' },
    { id: this.testTypes.Scan, type: 'Radiology' },
    { id: this.testTypes.Default, type: 'Both' },
  ];
  paymentMethods = [
    { id: 'PaymobCard', name: 'Paymob Card' },
    // { id: 'PaymobKiosk', name: 'Paymob Kiosk' },
  ];
  selectedPaymentMethod = 'Paymob Card'; // Default payment method

  constructor(
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private router: Router,
    private cityService: CityService,
    private areaService: AreaService,
    private patientService: PatientService,
    private medicalTestRequestService: MedicalTestRequestService,
    private modalService: BsModalService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.patientId = this.activatedRoute.snapshot.params.patientId;

    this.testAndLocationForm = this.formBuilder.group({
      testTypeId: [null],
      medicalTestsDetails: this.formBuilder.group({
        medicalTests: [null],
        testLocation: [this.testVisitTypes.Branch],
        city: [null],
        area: [null],
        address: [null],
      }),
      radiologyTestsDetails: this.formBuilder.group({
        radiologyTests: [null],
        testLocation: [this.testVisitTypes.Branch],
        city: [null],
        area: [null],
      })
    });

    this.labForm = this.formBuilder.group({
      testLab: [null, [Validators.required]],
      radiologyLab: [null, [Validators.required]]
    });

    this.paymentForm = this.formBuilder.group({
      paymentMethod: ['PaymobCard', [Validators.required]],
      promocode: [null],
    });

    this.getPatientById(this.patientId);

    this.testAndLocationForm.controls.medicalTestsDetails
      .get('testLocation').valueChanges.subscribe((value) => {
        this.handleMedicalTestLocationChangeValidators(value);
      });

    this.getMedicalTests();
  }

  handlePaymentMethodChange(): void {
    this.selectedPaymentMethod = this
      .paymentMethods
      .find(item => item.id === this.paymentForm.value?.paymentMethod).name;
  }

  onAddressChange(address: PatientAddress): void {
    this.selectedAddress = address;
  }

  generateAddressDetails(): string {
    let address = `
      ${this.selectedAddress.details},
      Building Number: ${this.selectedAddress.buildingNumber},
      Floor Number: ${this.selectedAddress.floorNumber}
    `;

    if (this.selectedAddress.apartmentNumber) {
      address += `, Apartment Number: ${this.selectedAddress.apartmentNumber}.`;
    }

    return address;
  }

  generateAddTestBody(): IBookTestRequestDTO {
    const patientId = this.patientId;
    const body: IBookTestRequestDTO = {} as IBookTestRequestDTO;
    body.patientId = +patientId;
    body.paymentMethod = this.paymentForm.value.paymentMethod;
    body.promoCode = this.paymentForm.value.promocode;
    body.medicalTestsDetails = null;
    body.radiologyTestsDetails = null;

    if (this.selectedTestTypeId === this.testTypes.Default || this.selectedTestTypeId === this.testTypes.Lab) {
      body.medicalTestsDetails = {
        testIds: this.testAndLocationForm.value.medicalTestsDetails.medicalTests.map(t => t.id),
        isHomeVisit: this.isHomeTest(),
        centerId: !this.labForm.value.testLab.branch?.id ? this.labForm.value.testLab.id : null,
        centerBranchId: this.labForm.value.testLab.branch?.id,
        patientAddressAreaId: this.isHomeTest() ? this.selectedAddress.areaId : null,
        patientAddress: this.isHomeTest() ? this.generateAddressDetails() : null,
      };
    }

    if (this.selectedTestTypeId === this.testTypes.Default || this.selectedTestTypeId === this.testTypes.Scan) {
      body.radiologyTestsDetails = {
        testIds: this.testAndLocationForm.value.radiologyTestsDetails.radiologyTests.map(t => t.id),
        centerBranchId: this.labForm.value.radiologyLab.branch?.id
      };
    }

    return body;
  }

  handleConfirmRequest(): void {
    const bodyObject = this.generateAddTestBody();

    this.medicalTestRequestService
      .addTestRequest(bodyObject)
      .subscribe((response) => {
        if (!response.data.id) {
          showNotification(
            'danger',
            'Something went wrong while adding the test request!',
            this.toastr
          );
          return;
        }

        const initialState: ModalOptions<any> = {
          keyboard: true,
          class: 'modal-dialog-centered',
          initialState: {
            patientId: this.patientId,
            testRequestId: response.data.id,
            orderMerchantId: response.data.referenceId
          },
        };

        this.modalRef = this.modalService.show(
          SuccessfullRequestModalComponent,
          initialState
        );

        this.modalRef.content.event.subscribe((actionValue) => {
          if (actionValue === 'create') {
            this.step = 1;
            this.testAndLocationForm.reset();
            this.labForm.reset();
            this.paymentForm.reset();
            this.selectedTestTypeId = null;

            this.testAndLocationForm.patchValue({
              testLocation: this.testVisitTypes.Branch,
            });
          }
        });
      });
  }

  isHomeTest(): boolean {
    return Number.parseInt(this.testAndLocationForm.value.medicalTestsDetails?.testLocation, 0) === this.testVisitTypes.Home;
  }

  getLabTestLocation(): string {
    return Number.parseInt(this.testAndLocationForm.value.medicalTestsDetails?.testLocation, 0) === this.testVisitTypes.Home
      ? 'At Home'
      : 'At Lab';
  }

  getRadiologyTestLocation(): string {
    return 'At Lab';
  }

  getLabTestsNames(): string {
    return this.testAndLocationForm?.value?.medicalTestsDetails?.medicalTests?.map((test) => test.name).join(', ');
  }

  getRadiologyTestsNames(): string {
    return this.testAndLocationForm?.value?.radiologyTestsDetails?.radiologyTests?.map((test) => test.name).join(', ');
  }

  getMedicalTests(): void {
    this.medicalTestRequestService.getMedicalTests().subscribe((response) => {
      const labTests = response.data.tests.filter((test) => test.testTypeId === this.testTypes.Lab);
      const radiologyTests = response.data.tests.filter(test => test.testTypeId === this.testTypes.Scan);

      this.labTests.push(...labTests);
      this.radiologyTests.push(...radiologyTests);
    });
  }

  getPatientById(patientId: string): void {
    this.patientService.getPateintById(patientId).subscribe((response) => {
      this.patient = response.data;
      this.addresses = this.patient.addresses;
      this.getAllCities();

      if (this.patient.personalInfo.cityId) {
        this.patient.personalInfo.cityName = this.cities.find(
          (city) => city.id === this.patient.personalInfo.cityId
        )?.enName;
      }

      if (this.patient.personalInfo.areaId) {
        this.areaService
          .getStringAreaName(this.patient.personalInfo.areaId)
          .subscribe((res) => {
            this.patient.personalInfo.areaName = res.data.enName;
          });
      }

      this.cityService.getAllCities().subscribe(cities => {
        this.patient.addresses.map((address) => {
          address.cityName = cities.find((city) => city.id === address.cityId)?.enName;

          this.areaService
            .getStringAreaName(address.areaId)
            .subscribe((res) => {
              address.areaName = res.data.enName;
            });

          return address;
        });
      });
    });
  }

  getAllCities(): void {
    this.cityService.getAllCities().subscribe((response) => {
      response.map((city: ICity) => {
        if (city.isActive) {
          this.cities.push(city);
        }
      });

      this.cityList = response;
      if (this.patient.personalInfo.cityId) {
        this.patient.personalInfo.cityName = this.cityList.find(
          (city) => city.id === this.patient.personalInfo.cityId
        )?.enName;
      }
      this.patient.addresses.map((address) => {
        address.cityName = this.cityList.find(
          (city) => city.id === address.cityId
        )?.enName;
        this.areaService.getStringAreaName(address.areaId).subscribe((res) => {
          address.areaName = res.data.enName;
        });
      });
    });
  }

  handleCityChange(cityId, testsTypeId): void {
    this.getAreasByCityId(cityId, testsTypeId);
  }

  handleMedicalTestsCityName() {
    this.selectedCity = this.cityList.filter(
      (city) => city.id == this.testAndLocationForm.controls.medicalTestsDetails.value.city
    )[0];
  }

  handleMedicalTestsAreaName() {
    this.selectedArea = this.selectedCity.areas.filter(
      (area) => area.id == this.testAndLocationForm.controls.medicalTestsDetails.value.area
    )[0];
  }

  handleRadiologyTestsCityName() {
    this.selectedCity = this.cityList.filter(
      (city) => city.id == this.testAndLocationForm.controls.radiologyTestsDetails.value.city
    )[0];
  }

  handleRadiologyTestsAreaName() {
    this.selectedArea = this.selectedCity.areas.filter(
      (area) => area.id == this.testAndLocationForm.controls.radiologyTestsDetails.value.area
    )[0];
  }

  getAreasByCityId(cityId, testsTypeId) {
    if (cityId) {
      if (testsTypeId == this.testTypes.Lab) {
        this.medicalTestsAreas = [];
        this.areaService.getAreasByCityId(cityId).subscribe((res) => {
          res?.data?.areas.map((area) => {
            if (area.isActive) {
              this.medicalTestsAreas.push(area);
            }
          });
        });
      }
      else {
        this.radiologyTestsAreas = [];
        this.areaService.getAreasByCityId(cityId).subscribe((res) => {
          res?.data?.areas.map((area) => {
            if (area.isActive) {
              this.radiologyTestsAreas.push(area);
            }
          });
        });
      }
    }
  }

  generateTestLabsParams(formValue) {
    const patientId = this.patientId;
    let areaId;
    let cityId;
    const testIds = JSON.stringify(
      formValue.medicalTestsDetails.medicalTests.map((test) => test.id)
    );
    const isVisitHome =
      formValue.medicalTestsDetails.testLocation == this.testVisitTypes.Home ? true : false;

    if (isVisitHome) {
      areaId = this.selectedAddress.areaId;
      cityId = this.selectedAddress.cityId;
    } else {
      areaId = formValue.medicalTestsDetails.area;
      cityId = formValue.medicalTestsDetails.city;
    }

    return {
      areaId,
      cityId,
      testIds,
      isVisitHome,
      patientId
    };
  }

  generateRadiologyLabsParams(formValue) {
    let areaId = formValue.radiologyTestsDetails.area;
    let cityId = formValue.radiologyTestsDetails.city;
    const testIds = JSON.stringify(
      formValue.radiologyTestsDetails.radiologyTests.map((test) => test.id)
    );
    const isVisitHome = false;
    const patientId = this.patientId;

    return {
      areaId,
      cityId,
      testIds,
      isVisitHome,
      patientId
    };
  }

  checkSelectedLab(lab: ILab): boolean {
    if (this.labForm?.value?.lab) {
      if (this.isHomeTest()) {
        return this.labForm.value.lab.id === lab.id;
      }

      return this.labForm.value.lab.id === lab.id && this.labForm.value.lab.branch.id === lab.branch.id;
    }

    return false;
  }

  handleTestLabSelect(lab) {
    this.labForm.controls.testLab.setValue(lab);
  }

  handleRadiologyLabSelect(lab) {
    this.labForm.controls.radiologyLab.setValue(lab);
  }

  onStepClick(value) {
    if (value == 1) {
      this.step = value;
    }

    if (value == 2) {
      if (this.testAndLocationForm.valid) {
        this.step = value;

        this.getLabsBasedOnTestTypeId();
      }
    }

    if (value == 3) {
      if (this.testAndLocationForm.valid && this.labForm.valid) {
        this.step = value;
      }
    }

    if (value == 4) {
      if (
        this.testAndLocationForm.valid &&
        this.labForm.valid &&
        this.paymentForm.valid &&
        this.isValidPromocode
      ) {
        this.step = value;
      }
    }
  }

  handleCancelTest() {
    this.router.navigate(['patient-details', this.patientId]);
    this.patientService.isMedicalTests.next(true);
  }

  handlePromecodeNextStep() {
    if (this.paymentForm.value.promocode) {
      this.checkPromocodeDiscount();
    } else {
      this.step += 1;
      this.discount = 0;
    }
  }

  onPromocodeKeyup(value) {
    if (value) {
      this.isValidPromocode = false;
    } else {
      this.isValidPromocode = true;
    }
  }

  checkPromocodeDiscount() {
    if (this.paymentForm.value.promocode) {
      let paramsObject = this.generatePromocodeParamas();

      let isHomeTest = this.isHomeTest();

      this.medicalTestRequestService
        .validateLabPromoCode(isHomeTest, paramsObject)
        .subscribe(
          (response) => {
            if (response?.data?.isPromoValid) {
              this.isValidPromocode = true;
              this.discount = response.data.fees.promoCodeDiscount;

              showNotification(
                'success',
                'This is a valid promocode.',
                this.toastr
              );

              this.step += 1;
            } else {
              this.isValidPromocode = false;
            }
          },
          (error) => {
            this.isValidPromocode = false;
          }
        );
    } else {
      this.isValidPromocode = true;
      this.discount = 0;
    }
  }

  generatePromocodeParamas() {
    const testIds = this.testAndLocationForm.value.medicalTests
      .map((test) => test.id)
      .join(',');
    const couponNameOrCode = this.paymentForm.value.promocode;
    const branchId = this.labForm.value.lab.branch?.id;
    const centerId = this.labForm.value.lab.id;
    let areaId;
    if (this.isHomeTest()) {
      areaId = this.selectedAddress.areaId;
    } else {
      areaId = this.testAndLocationForm.value.area;
    }
    const testVisitTypeId = this.testAndLocationForm.value.testLocation;

    return {
      testIds,
      couponNameOrCode,
      branchId,
      centerId,
      areaId,
      testVisitTypeId,
    };
  }

  handleNextStep() {
    this.step += 1;

    if (this.step == 2) {
      if (this.testAndLocationForm.valid) {
        this.getLabsBasedOnTestTypeId();
      }
    }
  }

  handlePrevStep() {
    this.step -= 1;
  }

  openNotificationModal(
    modalNotification: TemplateRef<any>,
    editMode = false,
    address = null,
    viewOnly = false,
    returnToSub = false
  ): void {
    this.newAddress = {} as PatientAddress;
    this.modalRef = this.modalService.show(
      modalNotification,
      this.addAddressForm
    );
  }

  onCityChange(cityId, flag = true, address = null) {
    if (cityId) {
      this.areaService.getAreasByCityId(cityId).subscribe((res) => {
        this.areaList = res?.data?.areas;
        if (flag) {
          this.newAddress.areaId = null;
        } else {
          this.newAddress = { ...address };
        }
        this.newAddress.cityId = cityId;
        this.newAddress.cityName = this.cityList.find(
          (city) => city.id === +cityId
        )?.enName;
      });
    }
  }

  onAreaChange(areaId) {
    if (areaId) {
      this.newAddress.areaId = areaId;
      this.newAddress.areaName = this.areaList.find(
        (area) => area.id === +areaId
      )?.enName;
    }
  }

  onSubmit(form: NgForm): void {
    const body = { patientId: this.patientId, ...this.newAddress };

    this.patientService.addAddress(body).subscribe((res) => {
      if (res) {
        let addedAddress = {} as PatientAddress;
        addedAddress = { ...res.data };
        addedAddress.cityName = this.cityList.find(
          (city) => city.id === res.data.cityId
        )?.enName;
        addedAddress.areaName = this.areaList.find(
          (area) => area.id === res.data.areaId
        )?.enName;
        this.patient.addresses.push(addedAddress);
        this.modalRef.hide();
      }
    });
  }

  handleSelectedTestType() {
    this.selectedTestTypeId = this.testAndLocationForm.value.testTypeId;
    this.handleTestsValidatorsBasedOnTestTypeId(this.selectedTestTypeId);
    this.handleTestsAddressesValidatorsBasedOnTestTypeId(this.selectedTestTypeId);
    this.handleCentersValidatorBasedOnTestTypeId(this.selectedTestTypeId);
  }

  handleTestsValidatorsBasedOnTestTypeId(testTypeId) {
    let medicalTestsFormControl = this.testAndLocationForm.get('medicalTestsDetails').get('medicalTests');
    let radiologyTestsFormControl = this.testAndLocationForm.get('radiologyTestsDetails').get('radiologyTests');
    if (testTypeId == this.testTypes.Default) {
      medicalTestsFormControl.setValidators([Validators.required]);
      radiologyTestsFormControl.setValidators([Validators.required]);
    }
    else if (testTypeId == this.testTypes.Lab) {
      radiologyTestsFormControl.clearValidators();
      radiologyTestsFormControl.patchValue(null);
      medicalTestsFormControl.setValidators([Validators.required]);
    }
    else if (testTypeId == this.testTypes.Scan) {
      medicalTestsFormControl.clearValidators();
      medicalTestsFormControl.patchValue(null);
      radiologyTestsFormControl.setValidators([Validators.required]);
    }
    medicalTestsFormControl.updateValueAndValidity();
    radiologyTestsFormControl.updateValueAndValidity();
  }

  handleTestsAddressesValidatorsBasedOnTestTypeId(testTypeId) {
    if (testTypeId == this.testTypes.Default) {
      this.addMedicalTestsAddressValidators();
      this.addRadiologyTestsAddressValidators();
    }
    else if (testTypeId == this.testTypes.Lab) {
      this.removeRadiologyTestsAddressValidators();
      this.removeRadiologyTestsAddressData();
      this.addMedicalTestsAddressValidators();
    }
    else if (testTypeId == this.testTypes.Scan) {
      this.removeMedicalTestsAddressValidators();
      this.removeMedicalTestsAddressData();
      this.addRadiologyTestsAddressValidators();
    }
  }

  addMedicalTestsAddressValidators() {
    let medicalTestsDetails = this.testAndLocationForm.controls.medicalTestsDetails;

    this.handleMedicalTestLocationChangeValidators(medicalTestsDetails.get('testLocation').value);
  }

  handleMedicalTestLocationChangeValidators(value) {
    let medicalTestsDetails = this.testAndLocationForm.controls.medicalTestsDetails;
    if (value == this.testVisitTypes.Home) {
      medicalTestsDetails.get('city').clearValidators();
      medicalTestsDetails.get('area').clearValidators();
      medicalTestsDetails.get('address').setValidators([
        Validators.required,
      ]);

      medicalTestsDetails.get('city').disable();
      medicalTestsDetails.get('area').disable();
      medicalTestsDetails.patchValue({
        city: null,
        area: null
      });
    }
    else {
      medicalTestsDetails.get('address').clearValidators();
      medicalTestsDetails.patchValue({
        address: null
      });

      medicalTestsDetails.get('city').enable();
      medicalTestsDetails.get('area').enable();
      medicalTestsDetails.get('city').setValidators([
        Validators.required,
      ]);
      medicalTestsDetails.get('area').setValidators([
        Validators.required,
      ]);
    }
    medicalTestsDetails.get('address').updateValueAndValidity();
    medicalTestsDetails.get('city').updateValueAndValidity();
    medicalTestsDetails.get('area').updateValueAndValidity();
  }

  addRadiologyTestsAddressValidators() {
    let radiologyTestsDetails = this.testAndLocationForm.controls.radiologyTestsDetails;

    radiologyTestsDetails.get('city').setValidators([Validators.required]);
    radiologyTestsDetails.get('area').setValidators([Validators.required]);

    radiologyTestsDetails.get('city').updateValueAndValidity();
    radiologyTestsDetails.get('area').updateValueAndValidity();
  }

  removeMedicalTestsAddressValidators() {
    let medicalTestsDetails = this.testAndLocationForm.controls.medicalTestsDetails;

    medicalTestsDetails.get('address').clearValidators();
    medicalTestsDetails.get('city').clearValidators();
    medicalTestsDetails.get('area').clearValidators();

    medicalTestsDetails.get('address').updateValueAndValidity();
    medicalTestsDetails.get('city').updateValueAndValidity();
    medicalTestsDetails.get('area').updateValueAndValidity();
  }

  removeRadiologyTestsAddressValidators() {
    let radiologyTestsDetails = this.testAndLocationForm.controls.radiologyTestsDetails;

    radiologyTestsDetails.get('city').clearValidators();
    radiologyTestsDetails.get('area').clearValidators();

    radiologyTestsDetails.get('city').updateValueAndValidity();
    radiologyTestsDetails.get('area').updateValueAndValidity();
  }

  removeMedicalTestsAddressData() {
    let medicalTestsDetails = this.testAndLocationForm.controls.medicalTestsDetails;

    medicalTestsDetails.patchValue({
      address: null,
      city: null,
      area: null
    });
  }

  removeRadiologyTestsAddressData() {
    let radiologyTestsDetails = this.testAndLocationForm.controls.radiologyTestsDetails;

    radiologyTestsDetails.patchValue({
      city: null,
      area: null
    });
  }

  getLabsBasedOnTestTypeId() {
    this.clearCentersData();
    if (this.selectedTestTypeId == this.testTypes.Default) {
      this.getRadiologyLabs(this.generateRadiologyLabsParams(this.testAndLocationForm.value));
      this.getTestLabs(this.generateTestLabsParams(this.testAndLocationForm.value));
    }
    else if (this.selectedTestTypeId == this.testTypes.Lab)
      this.getTestLabs(this.generateTestLabsParams(this.testAndLocationForm.value));

    else if (this.selectedTestTypeId == this.testTypes.Scan)
      this.getRadiologyLabs(this.generateRadiologyLabsParams(this.testAndLocationForm.value));
  }

  getTestLabs(paramsObject) {
    this.medicalTestRequestService
      .getLabs(paramsObject)
      .subscribe((response) => {
        this.testLabs = [];

        if (
          this.testAndLocationForm.value.medicalTestsDetails?.testLocation ==
          this.testVisitTypes.Home
        ) {
          response.data?.labs?.forEach((lab) => {
            this.testLabs.push(lab);
          });

          this.testLabs?.forEach((lab) => {
            if (this.checkSelectedLab(lab)) {
              this.labForm.controls.testLab.setValue(lab);
            }
          });
        } else {
          response.data?.labs?.map((lab) => {
            lab.branches.forEach((branch) => {
              let labItem = { ...lab };
              labItem['branch'] = branch;
              this.testLabs.push(labItem);
            });

            this.testLabs.forEach((lab) => {
              if (this.checkSelectedLab(lab)) {
                this.labForm.controls.testLab.setValue(lab);
              }
            });
          });
        }
      });
  }

  getRadiologyLabs(paramsObject) {
    this.medicalTestRequestService
      .getLabs(paramsObject)
      .subscribe((response) => {
        this.radiologyLabs = [];

        response.data?.labs?.map((lab) => {
          lab.branches.forEach((branch) => {
            let labItem = { ...lab };
            labItem['branch'] = branch;
            this.radiologyLabs.push(labItem);
          });

          this.radiologyLabs.forEach((lab) => {
            if (this.checkSelectedLab(lab)) {
              this.labForm.controls.radiologyLab.setValue(lab);
            }
          });
        });

      });
  }

  handleCentersValidatorBasedOnTestTypeId(testTypeId) {
    this.clearCentersData();
    let testLabFormControl = this.labForm.get('testLab');
    let radiologyLabFormControl = this.labForm.get('radiologyLab');
    if (testTypeId == this.testTypes.Default) {
      testLabFormControl.setValidators([Validators.required]);
      radiologyLabFormControl.setValidators([Validators.required]);
    }
    else if (testTypeId == this.testTypes.Lab) {
      radiologyLabFormControl.clearValidators();
      testLabFormControl.setValidators([Validators.required]);
    }
    else if (testTypeId == this.testTypes.Scan) {
      testLabFormControl.clearValidators();
      radiologyLabFormControl.setValidators([Validators.required]);
    }
    testLabFormControl.updateValueAndValidity();
    radiologyLabFormControl.updateValueAndValidity();
  }

  clearCentersData() {
    let testLabFormControl = this.labForm.get('testLab');
    let radiologyLabFormControl = this.labForm.get('radiologyLab');
    testLabFormControl.patchValue(null);
    radiologyLabFormControl.patchValue(null);
  }

  // Test method to verify toast notifications are working
  testToastNotification() {
    showNotification(
      'success',
      'Alexandria city has been Activated',
      this.toastr
    );
  }
}
